"use client";

import { CheckboxFormField } from "@/components/form/checkbox-form-field";
import { PhoneInputFormField } from "@/components/form/phone-input-form-field";
import { TextInputFormField } from "@/components/form/text-input-form-field";
import { Button } from "@/components/shadcn/button";
import { zodResolver } from "@hookform/resolvers/zod";
import { isValidPhoneNumber } from "libphonenumber-js";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useForm } from "react-hook-form";
import { z } from "zod";

// Messages come from the `events` catalog, so the schema is built per render
// with the request's translator instead of at module scope.
const buildWebinarFormSchema = (t: (key: string) => string) =>
    z.object({
        firstName: z.string().min(1, t("nameRequired")),
        lastName: z.string().min(1, t("lastNameRequired")),
        phoneNumber: z
            .string()
            .refine((val) => isValidPhoneNumber(val), t("phoneInvalid")),
        zoomUsername: z.string().min(1, t("zoomRequired")),
        marketingConsent: z.boolean().optional(),
        privacyConsent: z.boolean().refine((val) => val === true, {
            message: t("consentRequired"),
        }),
    });

type WebinarFormData = z.infer<ReturnType<typeof buildWebinarFormSchema>>;

interface WebinarFormProps {
    eventTitle: string;
    onSubmit: (data: WebinarFormData) => void | Promise<void>;
    onBack?: () => void;
    isSubmitting?: boolean;
}

export function WebinarForm({
    eventTitle,
    onSubmit,
    onBack,
    isSubmitting = false,
}: WebinarFormProps) {
    const t = useTranslations("events");
    const webinarFormSchema = buildWebinarFormSchema(t);

    const form = useForm<WebinarFormData>({
        resolver: zodResolver(webinarFormSchema),
        defaultValues: {
            firstName: "",
            lastName: "",
            phoneNumber: "",
            zoomUsername: "",
            marketingConsent: false,
            privacyConsent: false,
        },
    });

    const handleSubmit = form.handleSubmit(async (data) => {
        await onSubmit(data);
    });

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Event Title Display */}
            <div className="space-y-2">
                <label className="text-sm font-medium">{t("eventName")}</label>
                <div className="rounded-sm border border-neutral-300 bg-neutral-50 px-3 py-2 text-sm">
                    {eventTitle}
                </div>
            </div>

            {/* Name and Surname Row */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <TextInputFormField
                    control={form.control}
                    name="firstName"
                    label={t("name")}
                    placeholder={t("namePlaceholder")}
                    required
                    disabled={isSubmitting}
                />
                <TextInputFormField
                    control={form.control}
                    name="lastName"
                    label={t("lastName")}
                    placeholder={t("lastNamePlaceholder")}
                    required
                    disabled={isSubmitting}
                />
            </div>

            {/* Phone Number and Zoom Username Row */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <PhoneInputFormField
                    control={form.control}
                    name="phoneNumber"
                    label={t("phone")}
                    placeholder="(555) 000-0000"
                    required
                    disabled={isSubmitting}
                />
                <TextInputFormField
                    control={form.control}
                    name="zoomUsername"
                    label={t("zoomUsername")}
                    placeholder={t("zoomPlaceholder")}
                    description={t("zoomDescription")}
                    required
                    disabled={isSubmitting}
                />
            </div>

            {/* Marketing Consent Checkbox */}
            <CheckboxFormField
                control={form.control}
                name="marketingConsent"
                disabled={isSubmitting}
                label={
                    <span className="text-sm text-gray-600">
                        {t("marketingConsent")}
                    </span>
                }
            />

            {/* Privacy Consent Checkbox */}
            <CheckboxFormField
                control={form.control}
                name="privacyConsent"
                disabled={isSubmitting}
                label={
                    <span className="text-sm text-gray-600">
                        <Link
                            href="/sozlesmeler/aydinlatma-metni"
                            className="text-primary hover:underline"
                            target="_blank"
                        >
                            {t("consentLink")}
                        </Link>{" "}
                        {t("consentSuffix")}
                    </span>
                }
            />

            {/* Form Actions */}
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                {onBack && (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onBack}
                        disabled={isSubmitting}
                        className="w-full sm:w-auto"
                    >
                        {t("back")}
                    </Button>
                )}
                <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto"
                >
                    {isSubmitting ? t("submitting") : t("submit")}
                </Button>
            </div>
        </form>
    );
}
